import React, { useState, useMemo } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  AppBar,
  Toolbar,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Pagination,
  Stack,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import CloseIcon from "@mui/icons-material/Close";
import { enqueueSnackbar } from "notistack";
import axios from "axios";
import Header from "../Header";

const DuplicateSongs = () => {
  const [duplicates, setDuplicates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSongs, setSelectedSongs] = useState({});
  const [hasChecked, setHasChecked] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 10; // Number of duplicate sets to show per page

  // Function to check for duplicate songs
  const checkDuplicates = async () => {
    setLoading(true);
    setHasChecked(false);
    setPage(1); // Reset to first page when checking again
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/songs/duplicates`,
      );
      setDuplicates(response.data);
      setHasChecked(true);

      if (response.data.length === 0) {
        enqueueSnackbar("No duplicate songs found!", { variant: "success" });
      } else {
        enqueueSnackbar(
          `Found ${response.data.length} groups of duplicate songs`,
          { variant: "info" },
        );
      }
    } catch (error) {
      console.error("Error fetching duplicates:", error);
      enqueueSnackbar("Failed to fetch duplicate songs", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  // Pagination logic
  const paginatedDuplicates = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return duplicates.slice(startIndex, endIndex);
  }, [duplicates, page, itemsPerPage]);

  const totalPages = Math.ceil(duplicates.length / itemsPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle checkbox toggle for song selection
  const handleSelectSong = (groupIndex, songId) => {
    setSelectedSongs((prev) => {
      const key = `${groupIndex}-${songId}`;
      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  };

  // Handle select all in a group
  const handleSelectAllInGroup = (groupIndex, songs) => {
    setSelectedSongs((prev) => {
      const newSelection = { ...prev };
      const allSelected = songs.every(
        (song) => newSelection[`${groupIndex}-${song.songs_master_id}`],
      );

      songs.forEach((song) => {
        newSelection[`${groupIndex}-${song.songs_master_id}`] = !allSelected;
      });

      return newSelection;
    });
  };

  // Get count of selected songs
  const getSelectedCount = () => {
    return Object.values(selectedSongs).filter(Boolean).length;
  };

  // Open delete confirmation dialog
  const openDeleteDialog = () => {
    const count = getSelectedCount();
    if (count === 0) {
      enqueueSnackbar("Please select songs to delete", { variant: "warning" });
      return;
    }
    setDeleteDialogOpen(true);
  };

  // Handle delete selected songs
  const handleDeleteSelected = async () => {
    const count = getSelectedCount();
    if (count === 0) return;

    setDeleting(true);
    try {
      // Extract song IDs from selected songs
      const songIdsToDelete = Object.keys(selectedSongs)
        .filter((key) => selectedSongs[key])
        .map((key) => {
          const parts = key.split("-");
          return parseInt(parts[1]);
        });

      // Delete songs one by one
      const deletePromises = songIdsToDelete.map((songId) =>
        axios.delete(`${process.env.REACT_APP_API_URL}/api/songs/${songId}`),
      );

      await Promise.all(deletePromises);

      enqueueSnackbar(`Successfully deleted ${count} song(s)`, {
        variant: "success",
      });

      // Clear selection and re-check duplicates
      setSelectedSongs({});
      setDeleteDialogOpen(false);
      // Reset to first page after deletion
      setPage(1);
      console.log("checking");
      await checkDuplicates();
    } catch (error) {
      console.error("Error deleting songs:", error);
      enqueueSnackbar("Failed to delete some songs", { variant: "error" });
    } finally {
      setDeleting(false);
    }
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms && ms !== 0) return "N/A";
    // If already a formatted string like "mm:ss", return as-is
    if (typeof ms === "string") {
      // Trim and validate simple mm:ss pattern
      const trimmed = ms.trim();
      if (/^\d{1,2}:\d{2}$/.test(trimmed)) return trimmed;
      // Fallback: try to parse seconds if provided as numeric string
      const asNum = Number(trimmed);
      if (!Number.isNaN(asNum)) ms = asNum;
      else return trimmed;
    }
    if (typeof ms === "number") {
      // If looks like seconds, convert to ms
      const valueMs = ms < 1000 ? ms * 1000 : ms;
      const minutes = Math.floor(valueMs / 60000);
      const seconds = Math.floor((valueMs % 60000) / 1000);
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    }
    return "N/A";
  };

  return (
    <>
      <AppBar position="sticky" style={{ backgroundColor: "#121212" }}>
        <Toolbar>
          <Typography
            variant="h6"
            component="div"
            sx={{ flexGrow: 1, fontFamily: "Helvetica, Arial, sans-serif" }}
          >
            <Header />
          </Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        {/* Header Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <MusicNoteIcon fontSize="large" />
              Duplicate Songs Management
            </Typography>
            <Typography variant="body2" sx={{ color: "#999", mt: 1 }}>
              Find and manage duplicate songs in your library
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={
                loading ? <CircularProgress size={20} /> : <SearchIcon />
              }
              onClick={checkDuplicates}
              disabled={loading}
              sx={{
                backgroundColor: "#1DB954",
                "&:hover": { backgroundColor: "#1ed760" },
                textTransform: "none",
                fontWeight: "bold",
                px: 3,
              }}
            >
              {loading ? "Checking..." : "Check Duplicate Songs"}
            </Button>

            {getSelectedCount() > 0 && (
              <Button
                variant="contained"
                startIcon={<DeleteIcon />}
                onClick={openDeleteDialog}
                sx={{
                  backgroundColor: "#f44336",
                  "&:hover": { backgroundColor: "#d32f2f" },
                  textTransform: "none",
                  fontWeight: "bold",
                }}
              >
                Delete Selected ({getSelectedCount()})
              </Button>
            )}
          </Box>
        </Box>

        {/* Content Section */}
        {!hasChecked && !loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
            }}
          >
            <MusicNoteIcon sx={{ fontSize: 100, color: "#444", mb: 2 }} />
            <Typography variant="h6" sx={{ color: "#999", mb: 1 }}>
              Click "Check Duplicate Songs" to scan your library
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              We'll find songs with identical names and artists
            </Typography>
          </Box>
        )}

        {loading && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "400px",
            }}
          >
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6" sx={{ color: "#999" }}>
              Scanning for duplicates...
            </Typography>
          </Box>
        )}

        {hasChecked && !loading && duplicates.length === 0 && (
          <Alert severity="success" sx={{ mt: 2 }}>
            <Typography variant="h6">No Duplicates Found!</Typography>
            <Typography variant="body2">
              Your song library is clean. No duplicate songs detected.
            </Typography>
          </Alert>
        )}

        {hasChecked && !loading && duplicates.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Typography variant="h6" sx={{ color: "#fff" }}>
                Found {duplicates.length} Groups of Duplicates
              </Typography>
              {duplicates.length > itemsPerPage && (
                <Typography variant="body2" sx={{ color: "#999" }}>
                  Showing {(page - 1) * itemsPerPage + 1} -{" "}
                  {Math.min(page * itemsPerPage, duplicates.length)} of{" "}
                  {duplicates.length}
                </Typography>
              )}
            </Box>

            {paginatedDuplicates.map((group, index) => {
              // Calculate the actual group index in the full duplicates array
              const groupIndex = (page - 1) * itemsPerPage + index;
              return (
                <Card
                  key={groupIndex}
                  sx={{
                    mb: 3,
                    backgroundColor: "#1E1E1E",
                    borderRadius: 2,
                    border: "1px solid #333",
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{ color: "#1DB954", fontWeight: "bold" }}
                        >
                          {`Set ${groupIndex + 1}`}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#999" }}>
                          {group.artist ? `Artist: ${group.artist} • ` : ""}
                          {group.songs.length} songs in this set
                        </Typography>
                      </Box>
                      <Chip
                        label={`${group.songs.length} Versions`}
                        color="warning"
                        size="small"
                      />
                    </Box>

                    <TableContainer
                      component={Paper}
                      sx={{ backgroundColor: "#282828" }}
                    >
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell padding="checkbox">
                              <Checkbox
                                onChange={() =>
                                  handleSelectAllInGroup(
                                    groupIndex,
                                    group.songs,
                                  )
                                }
                                checked={group.songs.every(
                                  (song) =>
                                    selectedSongs[
                                      `${groupIndex}-${song.songs_master_id}`
                                    ],
                                )}
                                indeterminate={
                                  group.songs.some(
                                    (song) =>
                                      selectedSongs[
                                        `${groupIndex}-${song.songs_master_id}`
                                      ],
                                  ) &&
                                  !group.songs.every(
                                    (song) =>
                                      selectedSongs[
                                        `${groupIndex}-${song.songs_master_id}`
                                      ],
                                  )
                                }
                                sx={{
                                  color: "#999",
                                  "&.Mui-checked": { color: "#1DB954" },
                                }}
                              />
                            </TableCell>
                            <TableCell
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Song ID
                            </TableCell>
                            <TableCell
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Spotify ID
                            </TableCell>
                            <TableCell
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Song Name
                            </TableCell>
                            <TableCell
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Album
                            </TableCell>
                            <TableCell
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Duration
                            </TableCell>
                            <TableCell
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Year
                            </TableCell>
                            <TableCell
                              sx={{ color: "#fff", fontWeight: "bold" }}
                            >
                              Actions
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.songs.map((song) => (
                            <TableRow
                              key={song.songs_master_id}
                              sx={{
                                "&:hover": { backgroundColor: "#333" },
                                backgroundColor: selectedSongs[
                                  `${groupIndex}-${song.songs_master_id}`
                                ]
                                  ? "#2A2A2A"
                                  : "transparent",
                              }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox
                                  checked={
                                    selectedSongs[
                                      `${groupIndex}-${song.songs_master_id}`
                                    ] || false
                                  }
                                  onChange={() =>
                                    handleSelectSong(
                                      groupIndex,
                                      song.songs_master_id,
                                    )
                                  }
                                  sx={{
                                    color: "#999",
                                    "&.Mui-checked": { color: "#1DB954" },
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ color: "#fff" }}>
                                {song.songs_master_id}
                              </TableCell>
                              <TableCell sx={{ color: "#ccc" }}>
                                {song.spotify_id || "N/A"}
                              </TableCell>
                              <TableCell sx={{ color: "#fff" }}>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                  }}
                                >
                                  {song.image ? (
                                    <img
                                      src={song.image}
                                      alt={song.name}
                                      style={{
                                        width: 40,
                                        height: 40,
                                        objectFit: "cover",
                                        borderRadius: 4,
                                      }}
                                    />
                                  ) : (
                                    <Box
                                      sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: "#444",
                                        borderRadius: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <MusicNoteIcon sx={{ color: "#888" }} />
                                    </Box>
                                  )}
                                  <Box>
                                    <Typography
                                      sx={{ color: "#fff", fontWeight: 500 }}
                                    >
                                      {song.name || group.name}
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{ color: "#aaa" }}
                                    >
                                      {song.artist ||
                                        group.artist ||
                                        "Unknown Artist"}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: "#ccc" }}>
                                {song.album || "N/A"}
                              </TableCell>
                              <TableCell sx={{ color: "#ccc" }}>
                                {formatDuration(song.duration)}
                              </TableCell>
                              <TableCell sx={{ color: "#ccc" }}>
                                {song.release_year || "N/A"}
                              </TableCell>
                              <TableCell sx={{ color: "#ccc" }}>
                                <Button
                                  size="small"
                                  color="error"
                                  startIcon={<DeleteIcon />}
                                  onClick={async () => {
                                    try {
                                      await axios.delete(
                                        `${process.env.REACT_APP_API_URL}/api/songs/${song.songs_master_id}`,
                                      );
                                      enqueueSnackbar("Song deleted", {
                                        variant: "success",
                                      });
                                      await checkDuplicates();
                                    } catch (e) {
                                      enqueueSnackbar("Failed to delete song", {
                                        variant: "error",
                                      });
                                    }
                                  }}
                                >
                                  Delete
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <Box sx={{ mt: 2 }}>
                      <Alert severity="info" icon={false}>
                        <Typography variant="body2">
                          💡 <strong>Tip:</strong> Select the versions you want
                          to keep or remove. Check album, year, and duration to
                          identify the best version.
                        </Typography>
                      </Alert>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}

            {/* Pagination Controls */}
            {duplicates.length > itemsPerPage && (
              <Stack spacing={2} sx={{ mt: 4, mb: 2, alignItems: "center" }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="large"
                  showFirstButton
                  showLastButton
                  sx={{
                    "& .MuiPaginationItem-root": {
                      color: "#fff",
                      "&.Mui-selected": {
                        backgroundColor: "#1DB954",
                        color: "#fff",
                        "&:hover": {
                          backgroundColor: "#1ed760",
                        },
                      },
                      "&:hover": {
                        backgroundColor: "#333",
                      },
                    },
                  }}
                />
              </Stack>
            )}
          </Box>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          PaperProps={{
            sx: {
              backgroundColor: "#1E1E1E",
              color: "#fff",
            },
          }}
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <DeleteIcon color="error" />
            Confirm Deletion
            <IconButton
              onClick={() => setDeleteDialogOpen(false)}
              sx={{ ml: "auto", color: "#999" }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete {getSelectedCount()} selected
              song(s)?
            </Typography>
            <Typography variant="body2" sx={{ color: "#999", mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
              sx={{ color: "#999" }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteSelected}
              variant="contained"
              color="error"
              disabled={deleting}
              startIcon={
                deleting ? <CircularProgress size={20} /> : <DeleteIcon />
              }
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </>
  );
};

export default DuplicateSongs;
